// src/components/ExternalBookingModal.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import ReactModal from "react-modal";
import axios from "axios";
import { format } from "date-fns";
import toast from "react-hot-toast";

ReactModal.setAppElement("#root");

/* ───────────── Layout constants ───────────── */
const modalStyles = {
  content: {
    maxWidth: "2000px",
    width: "95%",
    margin: "auto",
    padding: 0,
    borderRadius: "12px",
    overflow: "hidden",
  },
  overlay: {
    backgroundColor: "rgba(0,0,0,.7)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
  },
};

/* ───────────── Little helpers ───────────── */
const fmt = (d) => (d ? format(new Date(d), "dd.MM.yyyy") : "—");
const colourForStatus = (s) =>
  ({
    New: "bg-yellow-400",
    Received: "bg-yellow-400",
    AwaitingCustomer: "bg-red-500",
    ReadyForService: "bg-green-500",
    Completed: "bg-gray-400",
    Closed: "bg-gray-400",
  }[s] || "bg-gray-400");

/* ════════════════════════════════════════════════
 *                 Counter widget
 * ════════════════════════════════════════════════*/
const Counter = ({ value, onChange, min = 0 }) => (
  <div className="inline-flex">
    <button
      onClick={() => onChange(Math.max(value - 1, min))}
      className="h-7 w-7 flex items-center justify-center rounded-l-full bg-gray-200 hover:bg-joule hover:text-white"
    >
      –
    </button>
    <input
      readOnly
      value={value}
      className="w-10 h-7 text-center border-t border-b border-gray-300"
    />
    <button
      onClick={() => onChange(value + 1)}
      className="h-7 w-7 flex items-center justify-center rounded-r-full bg-gray-200 hover:bg-joule hover:text-white"
    >
      +
    </button>
  </div>
);

/* ════════════════════════════════════════════════
 *            PartsSelector  (ALL parts, sortable + searchable)
 * ════════════════════════════════════════════════*/
const PartsSelector = ({
  selectedLines,
  setSelectedLines,
  disabled = false,
}) => {
  const [groups, setGroups] = useState({}); // { code: { name, parts } }
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  /* ───── fetch once ───── */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("/api/service-parts");
        const g = {};
        data.forEach((p) => {
          const code = p.groupCode || "Øvrige";
          if (!g[code]) g[code] = { name: p.groupName || "", parts: [] };
          g[code].parts.push(p);
        });
        setGroups(g);
      } catch (e) {
        console.error("[PartsSelector] fetch error:", e);
        toast.error("Could not load parts");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ───── helpers ───── */
  const qtyMap = useMemo(() => {
    const m = {};
    selectedLines.forEach((l) => (m[l.productNo] = l.quantity));
    return m;
  }, [selectedLines]);

  const updateLine = useCallback(
    (part, qty) => {
      if (disabled) return selectedLines; // ignore changes if disabled
      return setSelectedLines((prev) => {
        const idx = prev.findIndex((l) => l.productNo === part.productNo);
        if (qty === 0) return prev.filter((_, i) => i !== idx);

        const price =
          part.salesPriceIncVat ?? part.MRSPIncVat ?? part.salesPriceExVat ?? 0;

        const line = {
          lineType: "part",
          productNo: part.productNo,
          productName: part.productName,
          productDescription: part.productDescription,
          quantity: qty,
          salesPriceIncVat: price,
          lineTotal: price * qty,
          status: "proposed",
        };

        if (idx === -1) return [...prev, line];
        const next = [...prev];
        next[idx] = line;
        return next;
      });
    },
    [setSelectedLines, selectedLines, disabled]
  );

  /* ───── UI ───── */
  if (loading)
    return <p className="italic text-gray-500">Loading spare parts …</p>;

  const filterMatch = (p) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      p.productName.toLowerCase().includes(q) ||
      p.productNo.toLowerCase().includes(q)
    );
  };

  return (
    <>
      <div className="mb-4">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search part number or name…"
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring focus:border-joule"
          disabled={disabled}
        />
      </div>

      {Object.entries(groups)
        .sort(
          ([a], [b]) =>
            parseInt(a.match(/\d+/)?.[0] || "0", 10) -
            parseInt(b.match(/\d+/)?.[0] || "0", 10)
        )
        .map(([code, info]) => {
          if (code === "11") return null;

          const parts = info.parts.filter(filterMatch);
          if (parts.length === 0) return null;

          const open = expanded[code];
          return (
            <div key={code} className={`mb-4 ${disabled ? "opacity-60" : ""}`}>
              <button
                type="button"
                onClick={() =>
                  !disabled && setExpanded((p) => ({ ...p, [code]: !open }))
                }
                className={`w-full flex justify-between items-center px-4 py-2 rounded-md ${
                  disabled
                    ? "bg-gray-100 cursor-not-allowed"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
                disabled={disabled}
              >
                <span className="font-semibold">
                  {code} – {info.name} ({parts.length})
                </span>
                <span
                  className={`transform transition-transform ${
                    open ? "rotate-90" : ""
                  }`}
                >
                  ▶
                </span>
              </button>

              {open && (
                <div className="mt-3 space-y-3">
                  {parts.map((p) => {
                    const qty = qtyMap[p.productNo] || 0;
                    return (
                      <div
                        key={p.productNo}
                        className="flex items-center bg-gray-50 border border-gray-200 rounded-md p-2"
                      >
                        <input
                          type="checkbox"
                          className="h-5 w-5 accent-joule"
                          checked={qty > 0}
                          disabled={disabled}
                          onChange={(e) =>
                            updateLine(p, e.target.checked ? 1 : 0)
                          }
                        />
                        <label className="ml-3 flex-1">
                          {p.productName}{" "}
                          <span className="text-xs text-gray-500">
                            ({p.productNo})
                          </span>
                        </label>
                        {qty > 0 && (
                          <Counter
                            value={qty}
                            onChange={(v) => updateLine(p, v)}
                            min={1}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
    </>
  );
};

/* ════════════════════════════════════════════════
 *            Read‑only ServiceLine table
 * ════════════════════════════════════════════════*/
const ServiceLineTable = ({ lines = [] }) => {
  const total = useMemo(
    () => lines.reduce((sum, l) => sum + (l.lineTotal || 0), 0),
    [lines]
  );

  if (lines.length === 0)
    return <p className="italic text-gray-500">No service lines.</p>;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-collapse">
        <thead className="bg-gray-700 text-white">
          <tr>
            <th className="px-4 py-2 text-left">Type</th>
            <th className="px-4 py-2 text-left">Item</th>
            <th className="px-4 py-2 text-right">Qty</th>
            <th className="px-4 py-2 text-right">Total (kr)</th>
            <th className="px-4 py-2 text-left">Status</th>
            <th className="px-4 py-2 text-left">Added</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} className={i % 2 ? "bg-gray-50" : ""}>
              <td className="px-4 py-2">{l.lineType}</td>
              <td className="px-4 py-2">{l.productName}</td>
              <td className="px-4 py-2 text-right">{l.quantity}</td>
              <td className="px-4 py-2 text-right">
                {l.lineTotal?.toLocaleString("nb-NO")}
              </td>
              <td className="px-4 py-2">{l.status}</td>
              <td className="px-4 py-2">
                {l.createdAt ? format(new Date(l.createdAt), "dd.MM") : "—"}
              </td>
            </tr>
          ))}
          <tr className="font-semibold bg-gray-100">
            <td colSpan={3} className="px-4 py-2 text-right">
              Total
            </td>
            <td className="px-4 py-2 text-right">
              {total.toLocaleString("nb-NO")}
            </td>
            <td colSpan={2}></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

/* ════════════════════════════════════════════════
 *                Main ExternalBookingModal
 * ════════════════════════════════════════════════*/
const ExternalBookingModal = ({ isOpen, booking, closeModal, onUpdated }) => {
  const [localBooking, setLocalBooking] = useState(booking);
  const [lines, setLines] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showParts, setShowParts] = useState(false);

  /* keep a local copy of the booking prop */
  useEffect(() => {
    setLocalBooking(booking);
    setLines(booking?.serviceLines || []);
  }, [booking]);

  /* ---- status helpers ---- */
  const status = localBooking?.status;
  const isNew = status === "New";
  const isAwaiting = status === "AwaitingCustomer";
  const isReady = status === "ReadyForService";
  const isCompleted = status === "Completed";
  const isClosed = status === "Closed";
  const readOnly = isAwaiting || isCompleted || isClosed;

  /* derive step purely from status (no stale state) */
  const step = useMemo(
    () => (["ReadyForService", "Completed", "Closed"].includes(status) ? 2 : 1),
    [status]
  );

  /* ─── cost / quote logic ─── */
  const partsTotal = useMemo(
    () =>
      lines
        .filter((l) => l.lineType === "part")
        .reduce((sum, l) => sum + (l.lineTotal || 0), 0),
    [lines]
  );

  const needsQuote = useMemo(() => {
    if (partsTotal === 0) return false;
    if (localBooking?.autoApproveLimit > 0)
      return partsTotal > localBooking.autoApproveLimit;
    return true;
  }, [partsTotal, localBooking?.autoApproveLimit]);

  /* ─── package tasks ─── */
  const [packageTasks, setPackageTasks] = useState([]);
  useEffect(() => {
    if (!localBooking?.packageName) return;
    axios
      .get("/api/service-packages")
      .then(({ data }) => {
        const pkg = data.find((p) => p.name === localBooking.packageName);
        setPackageTasks(pkg?.includedTasksFull || []);
      })
      .catch((err) =>
        console.error(
          "[ExternalBookingModal] Could not load package tasks",
          err
        )
      );
  }, [localBooking?.packageName]);

  /* small helper to sync everything from server */
  const syncFromServer = (updated) => {
    setLocalBooking(updated);
    setLines(updated.serviceLines || []);
    onUpdated?.(updated);
  };

  /* ---- actions ---- */
  const markReceived = async () => {
    setIsSaving(true);
    try {
      const { data } = await axios.patch(
        `/api/external-bookings/${localBooking._id}/status`,
        { status: "Received" }
      );
      syncFromServer(data);
      toast.success("Sykkel registrert som mottatt");
    } catch (e) {
      console.error(e);
      toast.error("Kunne ikke markere som mottatt");
    } finally {
      setIsSaving(false);
    }
  };

  const saveInspection = async () => {
    setIsSaving(true);
    try {
      const prepared = lines.map((l) =>
        l.lineType !== "part"
          ? l
          : {
              ...l,
              status:
                localBooking.autoApproveLimit > 0
                  ? partsTotal <= localBooking.autoApproveLimit
                    ? "approved"
                    : "proposed"
                  : "proposed",
            }
      );

      const { data: updated } = await axios.patch(
        `/api/external-bookings/${localBooking._id}/inspection`,
        { serviceLines: prepared }
      );

      syncFromServer(updated);

      if (!needsQuote) {
        const { data: upd2 } = await axios.patch(
          `/api/external-bookings/${localBooking._id}/status`,
          { status: "ReadyForService" }
        );
        syncFromServer(upd2);
      } else {
        try {
          await axios.post(
            `/api/external-bookings/${localBooking._id}/send-quote-sms`
          );
        } catch (e) {
          console.error("[saveInspection] could not send quote SMS", e);
          toast.error("Could not send quote SMS");
        }
      }

      toast.success(
        needsQuote
          ? "Quote sent to customer (SMS)"
          : "Auto-approved – Ready for service"
      );
    } catch (e) {
      console.error(e);
      toast.error("Could not save inspection");
    } finally {
      setIsSaving(false);
    }
  };

  const markCompleted = async () => {
    setIsSaving(true);

    const approvedParts = lines.filter(
      (l) => l.lineType === "part" && l.status === "approved"
    );

    try {
      await Promise.all(
        approvedParts.map((p) =>
          axios.post(
            "/api/rackbeat/adjust-stock",
            {
              productNumber: p.productNo,
              quantity: -p.quantity,
              location: 1002,
            },
            { withCredentials: true }
          )
        )
      );

      const { data } = await axios.patch(
        `/api/external-bookings/${localBooking._id}/status`,
        { status: "Completed" }
      );

      syncFromServer(data);
      toast.success("Service marked completed & inventory updated");
      closeModal();
    } catch (err) {
      console.error("markCompleted error:", err);
      toast.error("Could not complete service (Rackbeat or status error)");
    } finally {
      setIsSaving(false);
    }
  };

  /* —— render —— */
  if (!localBooking) return null;

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={closeModal}
      style={modalStyles}
      contentLabel="External service"
    >
      <div className="flex flex-col sm:flex-row h-full">
        {/* —— Info column —— */}
        <aside className="sm:w-[26%] bg-gray-900 text-white p-6 space-y-6 overflow-y-auto text-[15px] leading-relaxed">
          <h2 className="text-2xl font-bold tracking-wide">
            #{localBooking.externalBookingId}
          </h2>

          <div className="space-y-0.5">
            <p className="font-semibold text-[17px]">
              {localBooking.firstName}&nbsp;{localBooking.lastName}
            </p>
            <p>{localBooking.phone}</p>
            <p>{localBooking.email}</p>
          </div>

          <div className="space-y-0.5">
            <p className="font-semibold text-gray-300">Bike</p>
            <p>
              {localBooking.bikeBrand}&nbsp;{localBooking.bikeModel}
            </p>
            {localBooking.frameNumber && (
              <p>Frame&nbsp;# {localBooking.frameNumber}</p>
            )}
          </div>

          <div className="space-y-0.5">
            <p className="font-semibold text-gray-300">Package</p>
            <p>
              {localBooking.packageName}&nbsp;–&nbsp;
              {localBooking.packagePrice.toLocaleString("nb-NO")} kr
            </p>
          </div>

          <div className="space-y-0.5">
            <p className="font-semibold text-gray-300">Delivery&nbsp;in</p>
            <p>
              {localBooking.deliveryType === "transport"
                ? "Transport"
                : "Drop‑off Nydalen"}
            </p>
            <p>
              {fmt(localBooking.bookingDate)}&nbsp;{localBooking.bookingTime}
            </p>
            {localBooking.deliveryType === "transport" && (
              <p>
                {localBooking.addressStreet},&nbsp;
                {localBooking.postalCode}&nbsp;{localBooking.city}
              </p>
            )}
          </div>

          {localBooking.returnDate && (
            <div className="space-y-0.5">
              <p className="font-semibold text-gray-300">Return booked</p>
              <p>
                {localBooking.returnType === "transport"
                  ? "Transport"
                  : "Pick‑up Nydalen"}
              </p>
              <p>
                {fmt(localBooking.returnDate)}&nbsp;{localBooking.returnTime}
              </p>
            </div>
          )}

          <div className="space-y-0.5 text-sm text-gray-200">
            <p className="font-semibold text-gray-300">Timeline</p>
            <p>Created:&nbsp;{fmt(localBooking.createdAt, true)}</p>
            <p>Received:&nbsp;{fmt(localBooking.receivedAt, true)}</p>
            <p>Inspection:&nbsp;{fmt(localBooking.inspectionDoneAt, true)}</p>
            <p>
              Customer&nbsp;reply:&nbsp;
              {fmt(localBooking.customerRespondedAt, true)}
            </p>
            <p>Completed:&nbsp;{fmt(localBooking.completedAt, true)}</p>
            <p>Returned:&nbsp;{fmt(localBooking.returnedAt, true)}</p>
          </div>

          {localBooking.autoApproveLimit > 0 && (
            <p className="text-sm text-joule">
              Auto‑approve parts&nbsp;≤&nbsp;{localBooking.autoApproveLimit} kr
            </p>
          )}

          {localBooking.note && (
            <div className="bg-gray-800 p-4 rounded whitespace-pre-line text-sm">
              <p className="font-semibold mb-1">Customer note</p>
              {localBooking.note}
            </div>
          )}

          <span
            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${colourForStatus(
              localBooking.status
            )}`}
          >
            {localBooking.status}
          </span>
        </aside>

        {/* —— Workspace —— */}
        <section className="sm:flex-1 p-6 overflow-y-auto">
          {isNew ? (
            <>
              <h3 className="text-lg font-semibold mb-4">
                Bike not received yet
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Du kan ikke gjøre noe før sykkelen er sjekket inn.
              </p>
              <button
                onClick={markReceived}
                disabled={isSaving}
                className="px-6 py-3 bg-joule text-white rounded hover:bg-gray-900 disabled:opacity-50"
              >
                Mark as received
              </button>
            </>
          ) : (
            <>
              {/* Step indicator */}
              <div className="flex items-center mb-6 space-x-4">
                {[1, 2].map((n) => (
                  <div key={n} className="flex items-center">
                    <div
                      className={`h-8 w-8 rounded-full flex items-center justify-center font-bold ${
                        step === n
                          ? "bg-joule text-white"
                          : "bg-gray-300 text-gray-600"
                      }`}
                    >
                      {n}
                    </div>
                    {n === 1 && (
                      <div className="w-10 border-t border-gray-400" />
                    )}
                  </div>
                ))}
              </div>

              {/* Step 1 – Inspection */}
              {step === 1 && (
                <>
                  <h3 className="text-lg font-semibold mb-4">Inspection</h3>

                  {isAwaiting && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800">
                      Quote sent to customer – Awaiting response. You can not
                      make any changes now
                    </div>
                  )}

                  {/* chips */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="px-2 py-1 text-xs rounded-full bg-gray-200">
                      Package&nbsp;
                      {localBooking.packagePrice.toLocaleString("nb-NO")} kr
                    </span>

                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        partsTotal === 0
                          ? "bg-gray-200 text-gray-600"
                          : needsQuote
                          ? "bg-red-200 text-red-700"
                          : "bg-green-200 text-green-700"
                      }`}
                    >
                      Parts&nbsp;{partsTotal.toLocaleString("nb-NO")} kr
                    </span>

                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        needsQuote
                          ? "bg-red-600 text-white"
                          : "bg-green-600 text-white"
                      }`}
                    >
                      {needsQuote ? "Quote required" : "Auto‑approved"}
                    </span>

                    {localBooking.autoApproveLimit > 0 && (
                      <span className="px-2 py-1 text-xs rounded-full bg-joule/10 text-joule">
                        Auto‑approve ≤ {localBooking.autoApproveLimit} kr
                      </span>
                    )}
                  </div>

                  {!readOnly && (
                    <div className="my-6">
                      <button
                        type="button"
                        aria-expanded={showParts}
                        onClick={() => setShowParts((p) => !p)}
                        className={`w-full flex items-center justify-between px-4 py-2 rounded-md border border-gray-200 bg-white shadow-sm
              hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-amber-300
              ${showParts ? "border-amber-400 bg-amber-50" : ""}`}
                      >
                        <span className="font-semibold relative pl-2 before:absolute before:left-[-12px] before:top-0 before:bottom-0 before:w-1 before:rounded before:bg-amber-400">
                          {showParts ? "Hide spare parts" : "Add spare parts"}
                        </span>
                        <span
                          className={`transition-transform ${
                            showParts
                              ? "rotate-90 text-amber-600"
                              : "text-gray-400"
                          }`}
                        >
                          ▶
                        </span>
                      </button>

                      {showParts && (
                        <div className="mt-4">
                          <PartsSelector
                            selectedLines={lines}
                            setSelectedLines={setLines}
                            disabled={readOnly}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <ServiceLineTable lines={lines} />

                  {!readOnly && (
                    <div className="flex justify-between mt-8">
                      <div>
                        <p>
                          <span className="font-semibold">Parts total:</span>{" "}
                          {partsTotal.toLocaleString("nb-NO")} kr
                        </p>
                        {localBooking.autoApproveLimit > 0 && (
                          <p className="text-sm text-gray-600">
                            Auto‑approve ≤ {localBooking.autoApproveLimit} kr
                          </p>
                        )}
                      </div>

                      <button
                        onClick={saveInspection}
                        disabled={isSaving}
                        className="px-6 py-3 bg-joule text-white rounded hover:bg-gray-900 disabled:opacity-50"
                      >
                        {needsQuote ? "Send quote" : "Save & continue"}
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Step 2 – Complete / Overview */}
              {step === 2 && (
                <>
                  <h3 className="text-lg font-semibold mb-6">
                    {isCompleted || isClosed
                      ? "Service overview"
                      : "Complete service"}
                  </h3>

                  {(() => {
                    const approved = lines.filter(
                      (l) => l.lineType === "part" && l.status === "approved"
                    );
                    const rejected = lines.filter(
                      (l) => l.lineType === "part" && l.status === "rejected"
                    );

                    const PartPanel = ({ title, tone, items }) => (
                      <section className="rounded-md shadow-sm bg-white">
                        <header
                          className={`flex items-center gap-2 px-4 py-2 rounded-t-md ${tone.bg}`}
                        >
                          <span className="text-lg">{tone.icon}</span>
                          <h4
                            className={`font-semibold tracking-wide ${tone.text}`}
                          >
                            {title}
                          </h4>
                        </header>

                        {items.length === 0 ? (
                          <p className="px-4 py-3 text-sm italic text-gray-500">
                            None
                          </p>
                        ) : (
                          <ul className="divide-y divide-gray-100">
                            {items.map((p) => (
                              <li
                                key={p.productNo}
                                className="flex justify-between px-4 py-2"
                              >
                                <div className="w-5/6">
                                  <p className="font-medium">{p.productName}</p>
                                  {p.productDescription && (
                                    <p className="text-xs text-gray-500">
                                      {p.productDescription}
                                    </p>
                                  )}
                                </div>
                                <span className="font-semibold">
                                  {p.quantity}×
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </section>
                    );

                    return (
                      <div className="grid md:grid-cols-2 gap-6">
                        <section className="bg-white border border-gray-200 rounded-md p-5 shadow-sm">
                          <h4 className="font-semibold mb-3 text-[17px]">
                            🔧 Tasks for “{localBooking.packageName}”
                          </h4>

                          {packageTasks.length === 0 ? (
                            <p className="text-sm text-gray-500">
                              No specific checklist for this package.
                            </p>
                          ) : (
                            <ul className="list-disc list-inside space-y-1 text-md">
                              {packageTasks.map((t) => (
                                <li key={t}>{t}</li>
                              ))}
                            </ul>
                          )}
                        </section>

                        <div className="space-y-6">
                          <PartPanel
                            title="Approved parts"
                            tone={{
                              bg: "bg-green-50",
                              text: "text-green-700",
                              icon: "✅",
                            }}
                            items={approved}
                          />
                          <PartPanel
                            title="Rejected parts"
                            tone={{
                              bg: "bg-red-50",
                              text: "text-red-700",
                              icon: "🚫",
                            }}
                            items={rejected}
                          />
                        </div>
                      </div>
                    );
                  })()}

                  {isReady && (
                    <button
                      onClick={markCompleted}
                      disabled={isSaving}
                      className="mt-8 px-10 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      Mark as completed
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </section>
      </div>
    </ReactModal>
  );
};

export default ExternalBookingModal;
